import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook to detect scroll position using Intersection Observer
 * This avoids conflicts with other scroll event listeners
 * @param {number} threshold - The scroll position threshold in pixels
 * @returns {boolean} - Whether the scroll position is past the threshold
 */
const useScrollPosition = (threshold = 78) => {
  const [isPastThreshold, setIsPastThreshold] = useState(false);
  const sentinelRef = useRef(null);

  useEffect(() => {
    // Check if Intersection Observer is supported
    if ('IntersectionObserver' in window) {
      // Use Intersection Observer (modern approach)
      const sentinel = document.createElement('div');
      sentinel.style.position = 'absolute';
      sentinel.style.top = `${threshold}px`;
      sentinel.style.left = '0';
      sentinel.style.width = '1px';
      sentinel.style.height = '1px';
      sentinel.style.pointerEvents = 'none';
      sentinel.style.zIndex = '-1';

      document.body.appendChild(sentinel);
      sentinelRef.current = sentinel;

      const observer = new IntersectionObserver(
        ([entry]) => {
          setIsPastThreshold(!entry.isIntersecting);
        },
        {
          root: null, // Use viewport as root
          rootMargin: '0px',
          threshold: 0,
        }
      );

      observer.observe(sentinel);

      return () => {
        observer.disconnect();
        if (sentinelRef.current && sentinelRef.current.parentNode) {
          sentinelRef.current.parentNode.removeChild(sentinelRef.current);
        }
      };
    } else {
      // Fallback: don't show the navbar in unsupported browsers
      // This keeps the component simple and avoids conflicts
      setIsPastThreshold(false);
      return () => {};
    }
  }, [threshold]);

  return isPastThreshold;
};

export default useScrollPosition;
